import { LANGUAGES, THEME_LABELS } from "@shared/i18n";
import { THEMES } from "@shared/themes";
import type { Language } from "@shared/types";
import { useI18n } from "../lib/i18n";
import { Icon } from "./Icon";

/**
 * Interface language. Per-device on purpose: the TV, the host's laptop and each
 * guest's phone can each be in a different language, and picking one here also
 * stops the room's default from overriding it later.
 */
export function LanguagePicker({ compact = false }: { compact?: boolean }) {
  const { lang, setLang, t } = useI18n();
  return (
    <div className="relative inline-flex items-center">
      <Icon
        name="globe"
        className="absolute left-2.5 text-ink/45 pointer-events-none"
        size="1rem"
      />
      <select
        value={lang}
        onChange={(e) => setLang(e.target.value as Language)}
        aria-label={t("language")}
        className="appearance-none bg-ink/[0.07] border border-ink/12 rounded-control h-9 pl-8 pr-7 text-sm text-ink/80 hover:bg-ink/[0.12] hover:text-ink transition-colors cursor-pointer"
      >
        {LANGUAGES.map((l) => (
          <option key={l.id} value={l.id}>
            {compact ? l.id.toUpperCase() : l.label}
          </option>
        ))}
      </select>
      <Icon
        name="chevronDown"
        className="absolute right-2 text-ink/40 pointer-events-none"
        size="0.9rem"
      />
    </div>
  );
}

/**
 * Theme. Unlike language this is a *room* setting: the host picks and every
 * screen in the room follows, so the TV and the phones always match.
 */
export function ThemePicker({
  value,
  onChange,
}: {
  value: string;
  onChange: (id: string) => void;
}) {
  const { lang, t } = useI18n();
  const labels = THEME_LABELS[lang] ?? THEME_LABELS.en;

  return (
    <fieldset>
      <legend className="type-eyebrow text-ink/45 mb-2 flex items-center gap-1.5">
        <Icon name="palette" size="0.9rem" />
        {t("theme")}
      </legend>
      <div className="flex flex-wrap gap-2">
        {THEMES.map((theme) => {
          const active = theme.id === value;
          return (
            <button
              key={theme.id}
              type="button"
              onClick={() => onChange(theme.id)}
              aria-pressed={active}
              className={`inline-flex items-center gap-2 pl-2 pr-3 h-9 rounded-control border text-sm transition-colors ${
                active
                  ? "border-accentLight bg-ink/[0.14] text-ink"
                  : "border-ink/12 text-ink/65 hover:bg-ink/[0.08] hover:text-ink"
              }`}
            >
              <span
                aria-hidden
                className="w-4 h-4 rounded-[5px] border border-black/25 shrink-0"
                style={{
                  background: `linear-gradient(135deg, ${theme.swatch[0]} 0 50%, ${theme.swatch[1]} 50% 100%)`,
                }}
              />
              {labels[theme.id] ?? theme.label}
            </button>
          );
        })}
      </div>
    </fieldset>
  );
}
